import { Injectable } from '@angular/core';
import { ByteBuffer } from 'flatbuffers';
import {
    collectRings,
    isPlausibleCoord,
    readFlatBufferString,
    ringsToFieldGeometry,
} from './boundary-geometry.util';
import {
    decodeGeographicUnitPayload,
    isGeographicUnitPayloadBuffer,
} from './geographic-unit-payload.decoder';
import type {
    FieldBoundaryCollection,
    FieldBoundaryFeature,
    FieldBoundaryProperties,
} from './field-boundary.types';

export type {
    FieldBoundaryCollection,
    FieldBoundaryFeature,
    FieldBoundaryProperties,
} from './field-boundary.types';

/**
 * Decodes crop plantation field boundary blobs (.bin) into GeoJSON.
 *
 * Supports:
 * - **GeographicUnitPayload** ([geographic_unit.fbs](schemas/geographic_unit.fbs)): GUID field ids, MultiPolygon geometry.
 * - **Legacy** Azure tiles: numeric field_id, boundary tree, optional record blocks at byte 16.
 */
@Injectable({ providedIn: 'root' })
export class FieldBoundaryDecoderService {
    decodeFields(_unitKey: string, buffer: ArrayBuffer): FieldBoundaryCollection {
        const bytes = new Uint8Array(buffer);
        if (bytes.length < 24) return this.empty();

        if (isGeographicUnitPayloadBuffer(bytes)) {
            return decodeGeographicUnitPayload(buffer);
        }

        return this.decodeLegacyFieldBlob(bytes);
    }

    mergeFeatureCollections(collections: FieldBoundaryCollection[]): FieldBoundaryCollection {
        const features: FieldBoundaryFeature[] = [];
        for (const fc of collections) {
            features.push(...fc.features);
        }
        return { type: 'FeatureCollection', features };
    }

    /** Logs root metadata to help reverse-engineer unknown .bin layouts. */
    probeBuffer(buffer: ArrayBuffer): void {
        const bytes = new Uint8Array(buffer);
        const bb = new ByteBuffer(bytes);
        const root = bb.readInt32(0);
        const zoomOff = bb.__offset(root, 4);
        const zoom = zoomOff ? bb.readInt32(root + zoomOff) : null;
        const fieldsVectorOff = bb.__offset(root, 6) || bb.__offset(root, 8);
        const count = fieldsVectorOff ? bb.__vector_len(root + fieldsVectorOff) : 0;
        console.info('[FieldBoundaryDecoder] probe', { zoom, fieldCount: count });
    }

    private decodeLegacyFieldBlob(bytes: Uint8Array): FieldBoundaryCollection {
        const bb = new ByteBuffer(bytes);
        const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
        const root = bb.readInt32(0);

        const fieldsVectorOff = bb.__offset(root, 6) || bb.__offset(root, 8);
        if (!fieldsVectorOff) return this.empty();

        const fieldsLen = bb.__vector_len(root + fieldsVectorOff);
        const fieldsStart = bb.__vector(root + fieldsVectorOff);
        if (fieldsLen <= 0) return this.empty();

        const recordBlocks = this.readRecordBlocks(dv, fieldsLen);

        const features: FieldBoundaryFeature[] = [];

        for (let i = 0; i < fieldsLen; i++) {
            const fieldPos = bb.__indirect(fieldsStart + i * 4);
            const fieldIdRaw = readFlatBufferString(bb, fieldPos, 4);
            const fieldId = fieldIdRaw != null ? Number.parseInt(fieldIdRaw, 10) : Number.NaN;
            if (!Number.isFinite(fieldId)) continue;

            let rings = this.readBoundaryRingsFromFieldTable(dv, bytes, fieldPos);
            if (rings.length === 0 && recordBlocks) {
                rings = this.scanBlockForRings(dv, bytes, recordBlocks[i]);
            }

            const coordinates = ringsToFieldGeometry(rings);
            if (!coordinates) continue;

            const properties: FieldBoundaryProperties = {
                field_id: fieldId,
                summer_crop_id: 0,
                winter_crop_id: 0,
                field_area: 0,
                city_id: 0,
                district_id: 0,
            };

            const isMulti = coordinates.length > 1 || (coordinates[0]?.length ?? 0) > 1;
            features.push({
                type: 'Feature',
                properties,
                geometry: isMulti
                    ? { type: 'MultiPolygon', coordinates }
                    : { type: 'Polygon', coordinates: coordinates[0]! },
            });
        }

        return { type: 'FeatureCollection', features };
    }

    private readRecordBlocks(dv: DataView, fieldsLen: number): { start: number; end: number }[] | null {
        if (fieldsLen <= 0 || dv.byteLength < 20 + fieldsLen * 4) return null;
        const countAt16 = dv.getInt32(16, true);
        if (countAt16 !== fieldsLen) return null;

        const offsets: number[] = [];
        for (let i = 0; i < fieldsLen; i++) {
            offsets.push(dv.getInt32(20 + i * 4, true));
        }

        const blocks: { start: number; end: number }[] = [];
        for (let i = 0; i < fieldsLen; i++) {
            const high = offsets[i];
            const low = i + 1 < fieldsLen ? offsets[i + 1] : 0;
            const start = Math.min(low, high);
            const end = Math.max(low, high);
            if (end <= start || end > dv.byteLength) {
                blocks.push({ start: 0, end: 0 });
            } else {
                blocks.push({ start, end });
            }
        }
        return blocks;
    }

    private readBoundaryRingsFromFieldTable(
        dv: DataView,
        bytes: Uint8Array,
        fieldPos: number,
    ): [number, number][][] {
        for (const slot of [8, 10, 12, 14, 16, 18, 20, 22]) {
            const rings = this.readBoundaryAtVtableSlot(dv, bytes, fieldPos, slot);
            if (rings.length > 0) return rings;
        }
        return [];
    }

    private readBoundaryAtVtableSlot(
        dv: DataView,
        bytes: Uint8Array,
        tablePos: number,
        vtableOff: number,
    ): [number, number][][] {
        const bb = new ByteBuffer(bytes);
        const boundaryTreeOff = bb.__offset(tablePos, vtableOff);
        if (!boundaryTreeOff) return [];

        const rel = dv.getInt32(tablePos + boundaryTreeOff, true);
        const treeRootPos = tablePos + boundaryTreeOff + rel;
        const rings: [number, number][][] = [];
        collectRings(dv, bytes, treeRootPos, 0, rings);
        return rings;
    }

    private scanBlockForRings(
        dv: DataView,
        bytes: Uint8Array,
        block: { start: number; end: number },
    ): [number, number][][] {
        if (!block || block.end <= block.start) return [];

        let best: [number, number][][] = [];
        for (let pos = block.start; pos < block.end - 8; pos += 4) {
            const rings: [number, number][][] = [];
            try {
                collectRings(dv, bytes, pos, 0, rings);
            } catch {
                continue;
            }
            if (rings.length > 0 && this.totalRingPoints(rings) > this.totalRingPoints(best)) {
                best = rings;
            }
        }

        if (best.length > 0) return best;

        return this.readInlineFloatRing(dv, block);
    }

    private readInlineFloatRing(
        dv: DataView,
        block: { start: number; end: number },
    ): [number, number][][] {
        const ring: [number, number][] = [];
        for (let p = block.start; p + 8 <= block.end; p += 8) {
            const lng = dv.getFloat32(p, true);
            const lat = dv.getFloat32(p + 4, true);
            if (!isPlausibleCoord(lng) || !isPlausibleCoord(lat)) {
                if (ring.length >= 3) break;
                ring.length = 0;
                continue;
            }
            ring.push([lng, lat]);
        }
        return ring.length >= 3 ? [ring] : [];
    }

    private totalRingPoints(rings: [number, number][][]): number {
        return rings.reduce((sum, ring) => sum + ring.length, 0);
    }

    private empty(): FieldBoundaryCollection {
        return { type: 'FeatureCollection', features: [] };
    }
}
